import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, Trash2, Layers, Gift, Coins, BookOpen, Copy, Edit2, Lock, CheckCircle2,
} from 'lucide-react';
import { SPACES, FONTS, SectionHeader, Card, SpaceBadge } from './creditsGuideShared';

const LibraryView = ({ library, assignments, onEdit, onDuplicate, onDelete, onAssign, onCreate, totalAssigned }) => {
  return (
    <div className="space-y-5">
      <SectionHeader
        icon={BookOpen}
        title="Bibliothèque de guides"
        subtitle={`${library.length} guide${library.length > 1 ? 's' : ''} · ${totalAssigned}/4 espaces assignés`}
        action={
          <Button onClick={onCreate} data-testid="create-guide-btn" className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white">
            <Plus className="h-4 w-4 mr-1" />Nouveau guide
          </Button>
        }
      />

      {/* ASSIGNMENTS MATRIX */}
      <Card className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 bg-[#fbbf24] rounded-full" />
          <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700" style={FONTS.outfit}>
            Assignations actives
          </h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {SPACES.map((sp) => {
            const guideId = assignments[sp.value];
            const guide = library.find((g) => g.id === guideId);
            return (
              <div
                key={sp.value}
                className="group relative rounded-lg border border-slate-200 bg-slate-50/40 p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <SpaceBadge space={sp.value} />
                  <span className="text-[10px] text-slate-400">{sp.sub}</span>
                </div>
                {guide ? (
                  <>
                    <div className="text-sm font-semibold text-slate-900 truncate" style={FONTS.outfit}>{guide.name}</div>
                    <Select value={guide.id} onValueChange={(newId) => onAssign(sp.value, newId)}>
                      <SelectTrigger className="mt-2 h-8 text-xs bg-white" data-testid={`assign-select-${sp.value}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {library.map((g) => (
                          <SelectItem key={g.id} value={g.id} className="text-xs">
                            {g.name}{g.is_factory ? ' (factory)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="text-xs text-rose-600">Aucun guide assigné</div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* LIBRARY GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {library.map((g) => {
          const assignedSpaces = g.assigned_spaces || [];
          return (
            <div
              key={g.id}
              className="group relative bg-white border border-slate-200 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
            >
              {/* Top gradient mini-banner */}
              <div
                className="h-16 relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${g.header?.color_from || '#0a1e3f'} 0%, ${g.header?.color_to || '#0B3C5D'} 100%)` }}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24]/60 to-transparent" />
                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-[#fbbf24]/10 blur-2xl" />
                <div className="relative h-full px-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white">
                    <Coins className="h-4 w-4 text-[#fbbf24]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-white/80" style={FONTS.outfit}>
                      {g.header?.title || 'Guide'}
                    </span>
                  </div>
                  {g.is_factory && (
                    <Badge className="bg-slate-900 text-white/80 text-[#fbbf24] border border-[#fbbf24]/40 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5">
                      <Lock className="h-2.5 w-2.5 mr-1" />FACTORY
                    </Badge>
                  )}
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-slate-900 truncate text-base" style={FONTS.outfit}>{g.name}</h4>
                    {g.description && (
                      <p className="text-xs text-slate-500 line-clamp-2 mt-1">{g.description}</p>
                    )}
                  </div>
                </div>

                {/* Stats mini */}
                <div className="flex items-center gap-3 mt-3 mb-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{(g.categories || []).length} cat.</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{(g.free_actions || []).length} gratuites</span>
                  <span className="flex items-center gap-1"><Gift className="h-3 w-3" />{(g.credit_types || []).length} types</span>
                </div>

                {/* Assigned spaces */}
                <div className="flex flex-wrap items-center gap-1 mb-3 min-h-[24px]">
                  {assignedSpaces.length > 0 ? (
                    assignedSpaces.map((sp) => <SpaceBadge key={sp} space={sp} />)
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">Non assigné</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-3 border-t border-slate-100">
                  <Button
                    size="sm"
                    onClick={() => onEdit(g.id)}
                    className="flex-1 bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white text-xs h-8"
                    data-testid={`edit-guide-${g.id}`}
                  >
                    <Edit2 className="h-3.5 w-3.5 mr-1" />Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDuplicate(g.id)}
                    className="h-8 px-2 border-slate-300"
                    title="Dupliquer"
                    data-testid={`duplicate-guide-${g.id}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(g)}
                    disabled={g.is_factory || assignedSpaces.length > 0}
                    className="h-8 px-2 border-slate-300 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 disabled:opacity-40"
                    title={g.is_factory ? 'Guide factory protégé' : assignedSpaces.length > 0 ? 'Réassignez avant de supprimer' : 'Supprimer'}
                    data-testid={`delete-guide-${g.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LibraryView;
